import { AInput } from "@/admin/form/Fields.tsx";
import { vmImageCreateMutation, type VmImageType, vmImageUpdateMutation } from "@/admin/vm/vmImagesQuery.ts";
import { WButton } from "@/components/button/WButton.tsx";
import { controllerProps } from "@/components/forms/ariaController.tsx";
import { FormField } from "@/components/forms/FormField.tsx";
import { IconRadio } from "@/components/forms/IconRadio.tsx";
import { WCheckbox } from "@/components/forms/WCheckbox.tsx";
import { WFileUploadField } from "@/components/forms/WFileUploadField.tsx";
import { WModalForm } from "@/components/forms/WModalForm.tsx";
import { WRadioGroup } from "@/components/forms/WRadioGroup.tsx";
import { WTextField } from "@/components/forms/WTextField.tsx";
import { WModal } from "@/components/modal/WModal.tsx";
import { WTable, WTableBody } from "@/components/table/WTable.tsx";
import { InfoTooltip, WTooltip } from "@/components/WTooltip.tsx";
import { useStandardMutation } from "@/modules/api/useStandardMutation.ts";
import { osIsWindows } from "@/modules/compute/os/os.ts";
import type { ItemType } from "@/modules/compute/vmCreateViewModel.tsx";
import type { VmImage, VmImageVersion } from "@warrenio/api-spec/spec.oats.gen";
import type { ReactNode } from "react";
import { Controller, type SubmitHandler, useForm } from "react-hook-form";

const types = [
    {
        id: "plain_os",
        title: "Operating system",
        icon: "jp-virtual-machine-icon",
        description: "",
    },
    {
        id: "app_catalog",
        title: "App catalog",
        icon: "i-lucide:blocks",
        description: "",
    },
] as const satisfies ItemType[];

export function AddEditVmImageButton({ button }: { button: ReactNode }) {
    return (
        <WModal button={button}>
            <AddEditVmImageModalContent />
        </WModal>
    );
}

export function EditHostPoolButton({ item, button }: { item: VmImage; button: ReactNode }) {
    return (
        <WModal button={button}>
            <AddEditVmImageModalContent item={item} />
        </WModal>
    );
}

interface AddEditVmImageInputs {
    image_type: VmImageType;
    os_name: string;
    display_name: string;
    file: string | undefined;
    is_default: boolean;
    license_type: string;
    versions: VmImageVersion[];
}

function AddEditVmImageModalContent({ item }: { item?: VmImage }) {
    const defaultVersions: VmImageVersion[] = [
        {
            os_version: "",
            display_name: "",
            published: false,
            boot_firmware: "bios",
        },
    ];

    const createMutation = useStandardMutation(vmImageCreateMutation);
    const updateMutation = useStandardMutation(vmImageUpdateMutation);

    const defaultOsName = item?.os_name ?? "";
    const isWindows = osIsWindows(defaultOsName);
    const isEditMode = !!item;
    const availableTypes = types;

    const onSubmit: SubmitHandler<AddEditVmImageInputs> = async (inputs) => {
        const { image_type, os_name, display_name, is_default, license_type, file, versions } = inputs;

        const body: VmImage = {
            os_name,
            display_name,
            ui_position: item?.ui_position ?? 0,
            is_default,
            is_app_catalog: item?.is_app_catalog ?? false,
            icon: file || "",
            versions,
            ...(isWindows && { license_type }),
        };

        if (isEditMode) {
            await updateMutation.mutateAsync({
                body,
                os_name,
                image_type,
            });
        } else {
            await createMutation.mutateAsync({
                body,
                image_type,
            });
        }
    };

    function addVersion() {
        const items = [...getValues("versions"), ...defaultVersions];
        setValue("versions", items);
    }

    function deleteVersion(index: number) {
        const items = [...getValues("versions")];
        items.splice(index, 1);

        setValue("versions", items);
    }

    function onChangeVersion(
        index: number,
        key: "os_version" | "display_name" | "published" | "boot_firmware",
        value: string | boolean,
    ) {
        const items = getValues("versions").map((item, i) => (i === index ? { ...item, [key]: value } : item));
        setValue("versions", items);
    }

    const form = useForm<AddEditVmImageInputs>({
        mode: "onBlur",
        reValidateMode: "onBlur",

        defaultValues: {
            image_type: item?.is_app_catalog ? "app_catalog" : "plain_os",
            os_name: defaultOsName,
            display_name: item?.display_name ?? "",
            file: item?.icon,
            is_default: item?.is_default ?? false,
            license_type: item?.license_type ?? "",
            versions: item?.versions ?? defaultVersions,
        },
    });

    const { control, setValue, getValues } = form;

    return (
        <WModalForm
            title={isEditMode ? `Update ${item.display_name}` : "Create New VM image"}
            label={isEditMode ? "Edit" : "Create"}
            form={form}
            onSubmit={onSubmit}
        >
            <Controller
                control={control}
                name="image_type"
                {...controllerProps({ isRequired: true })}
                render={({ field }) => (
                    <WRadioGroup
                        label="Type"
                        isReadOnly={isEditMode}
                        isRequired
                        headless
                        value={field.value}
                        onChange={field.onChange}
                    >
                        {availableTypes.map((item) => (
                            <IconRadio key={item.id} {...item} />
                        ))}
                    </WRadioGroup>
                )}
            />

            <Controller
                control={control}
                name="os_name"
                {...controllerProps({ isRequired: true })}
                render={({ field }) => (
                    <WTextField
                        headless
                        isReadOnly={isEditMode}
                        isRequired
                        label="Name"
                        value={field.value}
                        onChange={field.onChange}
                        description="Full image name will be <name>_<version>, this is the <name> prefix. Must be unique, cannot be changed."
                    >
                        <AInput />
                    </WTextField>
                )}
            />

            <Controller
                control={control}
                name="display_name"
                {...controllerProps({ isRequired: true })}
                render={({ field }) => (
                    <WTextField
                        isRequired
                        headless
                        label="Display Name"
                        value={field.value}
                        onChange={field.onChange}
                        description="User friendly name shown in UI."
                    >
                        <AInput />
                    </WTextField>
                )}
            />

            <Controller
                control={control}
                name="file"
                render={({ field }) => (
                    <WFileUploadField
                        headless
                        onChange={field.onChange}
                        value={field.value ?? ""}
                        label="Icon"
                        description="Icon shown in UI. Accepted format is SVG and file size under 10kb."
                    />
                )}
            />

            <Controller
                control={control}
                name="is_default"
                render={({ field }) => (
                    <FormField label="Set as Default" wide headless>
                        <WCheckbox isSelected={field.value} onChange={field.onChange}>
                            When checked current image will be preselected in UI.
                        </WCheckbox>
                    </FormField>
                )}
            />

            {isWindows && (
                <Controller
                    control={control}
                    name="license_type"
                    render={({ field }) => (
                        <FormField label="Set as Licensed" wide headless>
                            <WCheckbox
                                isSelected={field.value === "WINDOWS"}
                                onChange={(value) => field.onChange(value ? "WINDOWS" : "")}
                            >
                                When checked a Windows license fee is added for this image.
                            </WCheckbox>
                        </FormField>
                    )}
                />
            )}

            <Controller
                control={control}
                name="versions"
                {...controllerProps({ isRequired: true })}
                render={({ field }) => (
                    <FormField label="Versions" wide headless isRequired>
                        <WTable>
                            <thead>
                                <tr>
                                    <th>
                                        Version
                                        <InfoTooltip
                                            className="ml-1"
                                            text="Full image name will be &lt;name&gt;_&lt;version&gt;, this is the &lt;version&gt; suffix."
                                        />
                                    </th>
                                    <th>
                                        Display Name
                                        <InfoTooltip className="ml-1" text="User friendly version shown in UI." />
                                    </th>
                                    <th>
                                        UEFI
                                        <InfoTooltip
                                            className="ml-1"
                                            text="Requires UEFI firmware to boot. If unchecked, legacy BIOS is used."
                                        />
                                    </th>
                                    <th>
                                        Published
                                        <InfoTooltip
                                            className="ml-1"
                                            text="Unpublished version will be visible only for admins."
                                        />
                                    </th>
                                    <th />
                                </tr>
                            </thead>
                            <WTableBody>
                                {field.value.map((_item, index) => (
                                    <tr key={index}>
                                        <td>
                                            <WTextField
                                                headless
                                                wide
                                                isRequired
                                                aria-label="Version"
                                                value={field.value[index].os_version}
                                                onChange={(value) => onChangeVersion(index, "os_version", value)}
                                            >
                                                <AInput />
                                            </WTextField>
                                        </td>
                                        <td>
                                            <WTextField
                                                headless
                                                wide
                                                isRequired
                                                aria-label="Display Name"
                                                value={field.value[index].display_name}
                                                onChange={(value) => onChangeVersion(index, "display_name", value)}
                                            >
                                                <AInput />
                                            </WTextField>
                                        </td>
                                        <td>
                                            <WCheckbox
                                                isSelected={field.value[index].boot_firmware === "uefi"}
                                                onChange={(value) =>
                                                    onChangeVersion(index, "boot_firmware", value ? "uefi" : "bios")
                                                }
                                            >
                                                UEFI
                                            </WCheckbox>
                                        </td>
                                        <td>
                                            <WCheckbox
                                                isSelected={field.value[index].published}
                                                onChange={(value) => onChangeVersion(index, "published", value)}
                                            >
                                                Published
                                            </WCheckbox>
                                        </td>
                                        <td>
                                            <div className="flex gap-0.5em justify-end">
                                                <WTooltip text="Delete">
                                                    <WButton
                                                        action={() => deleteVersion(index)}
                                                        size="xs"
                                                        color="primary"
                                                        variant="ghost"
                                                        ariaLabel="Delete"
                                                        icon="jp-trash-icon"
                                                    />
                                                </WTooltip>
                                            </div>
                                        </td>
                                    </tr>
                                ))}
                            </WTableBody>
                        </WTable>

                        <div className="pt-2">
                            <WButton
                                className="w-full"
                                label="Add New Version"
                                color="primary"
                                size="md"
                                variant="dashed"
                                icon="jp-icon-add"
                                action={() => addVersion()}
                            />
                        </div>
                    </FormField>
                )}
            />
        </WModalForm>
    );
}
